/**
 * Shared types used across the widget. Field names follow the server-side
 * Zod schemas in `@helpdesk/shared` (snake_case for persisted entities, so
 * payloads coming over the wire map 1:1 without a translation layer).
 */

export type Role = 'customer' | 'ai' | 'agent' | 'system';

export type MessageStatus = 'sending' | 'sent' | 'failed';

export interface Attachment {
  storage_key: string;
  content_type: string;
  size_bytes: number;
  original_name?: string;
  /** Local-only URL (object URL or remote preview) used for thumbnails */
  preview_url?: string;
}

export interface Message {
  id: string;
  role: Role;
  text: string;
  attachments: Attachment[];
  created_at: string;
  /** Optional client-side status for optimistic rendering */
  status?: MessageStatus;
  /** AI confidence score 0..1 — present on 'ai' messages when available */
  confidence?: number;
}

export interface Faq {
  id: string;
  question: string;
  answer: string;
  tags: string[];
}

export type WidgetPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export type Locale = 'en' | 'es';

export type WidgetShape = 'circle' | 'rounded-square' | 'pill';
export type WidgetSize = 'sm' | 'md' | 'lg';
export type WidgetAnimation = 'none' | 'pulse' | 'bounce' | 'breath' | 'wave';

export interface Branding {
  primary_color?: string;
  logo_url?: string;
  greeting?: string;
  locale: Locale;
  position: WidgetPosition;
  assistant_name?: string;
  assistant_avatar_url?: string;
  assistant_tagline?: string;
  widget_shape?: WidgetShape;
  widget_size?: WidgetSize;
  widget_animation?: WidgetAnimation;
  widget_image_url?: string;
  /** Optional welcome bubble shown next to the FAB on page load. */
  welcome_message?: string;
  /** Auto-hide delay in seconds. 0 = never auto-hide. */
  welcome_timeout?: number;
  /** When true (or omitted), play a chime on each AI/agent reply. */
  notification_sound?: boolean;
  /** When false, hides the attach-file button in the composer. Default: true. */
  allow_attachments?: boolean;
  /**
   * Pre-configured opening question + click-to-send answer chips shown
   * before the user types. When `hide_faqs` is true the chip view replaces
   * the FAQ list entirely (typical for landing pages).
   */
  welcome_chips?: {
    enabled?: boolean;
    question?: string;
    options?: string[];
    hide_faqs?: boolean;
  };
}

export interface Customer {
  id: string;
  external_id?: string;
  name?: string;
  email?: string;
}

export interface PageContext {
  url: string;
  title?: string;
}

export type CaptureFieldName = 'name' | 'email' | 'business' | 'phone';
export type HandoffFieldType = 'text' | 'email' | 'phone' | 'textarea';

export const BUILTIN_HANDOFF_FIELDS: readonly CaptureFieldName[] = [
  'name',
  'email',
  'business',
  'phone',
];

export function isBuiltinHandoffField(k: string): k is CaptureFieldName {
  return (BUILTIN_HANDOFF_FIELDS as readonly string[]).includes(k);
}

export interface CaptureFieldConfig {
  /** Built-in key OR custom snake_case key (server enforces format). */
  field: string;
  enabled: boolean;
  required: boolean;
  label: string;
  /** Used for custom fields only. Built-ins infer their type. */
  type?: HandoffFieldType;
  placeholder?: string;
}

export interface DataSourceConfig {
  target: 'name' | 'email' | 'business' | 'external_id';
  from: 'cookie' | 'localStorage' | 'window';
  key: string;
}

export type LeadFieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'url'
  | 'number'
  | 'textarea'
  | 'select';

export type LeadTrigger = 'on_resolve' | 'after_n_turns' | 'ai_decision';

export interface LeadFieldConfig {
  key: string;
  label: string;
  type: LeadFieldType;
  required: boolean;
  placeholder?: string;
  options?: string[];
}

export interface LeadsCaptureConfig {
  enabled: boolean;
  trigger: LeadTrigger;
  turnThreshold: number;
  fields: LeadFieldConfig[];
  title?: string;
  submitLabel?: string;
  closeMessage?: string;
}

export type HandoffMode = 'inline' | 'form';

export interface CaptureConfig {
  handoff: {
    enabled: boolean;
    /**
     * How the widget collects required contact fields before handing off.
     * Optional for backwards compatibility with dashboards that don't yet
     * send the field — the widget treats `undefined` as `'inline'`.
     */
    mode?: HandoffMode;
    title?: string;
    submitLabel?: string;
    fields: CaptureFieldConfig[];
  };
  sources: DataSourceConfig[];
  leads?: LeadsCaptureConfig;
  /**
   * Optional scripted flow that replaces the AI with a pre-programmed
   * sequence of questions. Absent = no script; `enabled=false` = configured
   * but disabled (retained so the tenant can re-enable without losing steps).
   */
  script?: ScriptConfig;
}

export type ScriptStepType = 'text' | 'choice';

export interface ScriptChoice {
  label: string;
  nextStepId: string | null;
}

export interface ScriptStep {
  id: string;
  prompt: string;
  answerKey: string;
  type: ScriptStepType;
  choices: ScriptChoice[];
  nextStepId: string | null;
}

export interface ScriptConfig {
  enabled: boolean;
  triggers: {
    onOpen: boolean;
    onHandoff: boolean;
  };
  steps: ScriptStep[];
  firstStepId: string | null;
  closing?: string;
  handoffAfter: boolean;
}

export type ScriptTrigger = 'on_open' | 'on_handoff';

export interface HandoffContact {
  name?: string;
  email?: string;
  business?: string;
  phone?: string;
  /** Values for tenant-defined custom fields (e.g. `{ dni: '...' }`). */
  custom?: Record<string, string>;
}

/**
 * Verified identity claims that may come back with the session. The widget
 * uses these to decide whether the handoff form can be skipped.
 */
export interface SessionClaims {
  eml?: string;
  nam?: string;
  met?: Record<string, unknown>;
}

export interface SessionPayload {
  serviceId: string;
  tenantId: string;
  branding: Branding;
  faqs: Faq[];
  customer?: Customer;
  capture?: CaptureConfig;
  claims?: SessionClaims;
  /**
   * Soft block — the tenant exhausted their plan's conversation quota and
   * a brand-new conversation cannot be created. The widget renders an
   * unobtrusive "service unavailable" notice and disables the composer.
   */
  blocked?: boolean;
  conversation?: {
    id: string;
    status?:
      | 'ai_handling'
      | 'awaiting_human'
      | 'assigned'
      | 'resolved'
      | 'abandoned';
    messages?: Message[];
  };
}

export type ChatStreamEvent =
  | { type: 'delta'; text: string }
  | {
      type: 'done';
      meta: {
        confidence: number;
        sources?: Array<{ id?: string; title?: string; url?: string; index?: number }>;
        conversation_id: string;
        message_id: string | null;
        /** Hard handoff trigger — widget transfers immediately. */
        needsHumanHandoff?: boolean;
        /**
         * Soft proposal — the LLM suggested a handoff but the widget should
         * only fire it if the user confirms on their NEXT message. Bot's
         * answer already ends with the confirmation question.
         */
        proposeHandoff?: boolean;
      };
    }
  | { type: 'error'; error: string }
  | { type: 'blocked'; error: 'plan_limit_reached'; message: string };

export type ViewName =
  | 'faq'
  | 'chat'
  | 'connecting'
  | 'error'
  | 'handoff_form'
  | 'lead_capture_form';

export interface PresignResponse {
  uploadUrl: string;
  storage_key: string;
}
